import { PAGE_START } from '~/common/constants/page';
import { Highlight } from '~/features/document-viewer/types/highlight';
import { PromptModelTestCase } from '~/features/x-ray/types/deprecated';
import { sylvanus } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import {
  FieldValueSource,
  HighlightingCoordinates,
  PromptModelTestCase as PromptModelTestCaseResponse,
} from '../../types/sylvanus/model';
import { mapTestCase } from './transformers';

interface Request {
  modelId: Uuid;
  version: number;
  highlightMethod: string;
  testCaseNumber: number;
  outcome: PromptModelTestCase['outcome'];
  feedback?: Nullable<string>;
  goldValue?: PromptModelTestCase['goldValue'];
}

type Response = PromptModelTestCaseResponse;

export const updatePromptModelTestCase = async ({
  modelId,
  version,
  highlightMethod,
  testCaseNumber,
  outcome,
  feedback,
  goldValue,
}: Request): Promise<PromptModelTestCase> => {
  const response = (await sylvanus.patch(
    `/model/${modelId}/version/${version}/testcase/${testCaseNumber}`,
    {
      // FE should always send `partially_correct` outcomes as `incorrect` - BE handles the conversion.
      outcome: outcome === 'partially_correct' ? 'incorrect' : outcome,
      gold_value: goldValue
        ? {
            value: goldValue.value,
            value_type:
              goldValue.type === 'classification-unknown'
                ? 'unknown'
                : goldValue.type,
            output_type:
              goldValue.type === 'classification' ||
              goldValue.type === 'classification-unknown'
                ? 'string'
                : 'field_value',
            // only set the sources if the highlights array is well-defined
            ...(goldValue.highlights
              ? {
                  sources: mapHighlightsToFieldValueSources(
                    goldValue.highlights,
                  ),
                }
              : {}),
          }
        : null,
      user_feedback: feedback || null,
    },
  )) as Response;

  return mapTestCase(response, highlightMethod);
};

const mapHighlightingCoordinates = ({
  pdfCoordinates,
  htmlTokens,
  highlighter,
}: Highlight): HighlightingCoordinates[] => {
  return [
    {
      coordinates: {
        rects:
          pdfCoordinates?.rects?.map((rect) => ({
            ...rect,
            pageNumber:
              rect.pageNumber ?? pdfCoordinates?.pageNumber ?? PAGE_START,
            pageRotation: 0,
          })) ?? [],
        pageNumber: pdfCoordinates?.pageNumber ?? PAGE_START,
      },
      source: highlighter ?? 'ai',
      token_ids: htmlTokens ?? [],
    },
  ];
};

const mapHighlightsToFieldValueSources = (
  highlights: Highlight[],
): FieldValueSource[] =>
  highlights.map((highlight) => ({
    context: {
      coordinates: mapHighlightingCoordinates(highlight),
      html_tokens: highlight.htmlTokens,
      text: highlight.text,
    },
    generated_by: (highlight as Highlight<FieldValueSource>).data?.generated_by,
  }));
